import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Typography,
} from '@mui/material';
import {
  CameraAlt,
  Close,
  CloudUpload,
  Description,
  InsertDriveFile,
  PhotoLibrary,
} from '@mui/icons-material';

const MilestoneUploadCard = ({
  title,
  hint,
  emptyLabel,
  accept = 'image/*',
  capture = false,
  files = [],
  disabled = false,
  uploading = false,
  onUpload,
  onDelete,
  downloadHref,
  onOpenFile,
  icon = 'camera',
}) => {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');

  const openFilePicker = () => fileInputRef.current?.click();
  const openCamera = () => cameraInputRef.current?.click();
  const openPrimary = () => (capture ? openCamera() : openFilePicker());

  const handleFiles = async (list) => {
    setError('');
    const file = list?.[0];
    if (!file) return;
    try {
      await onUpload(file);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Upload failed');
    }
  };

  return (
    <Box
      sx={{
        border: '1px dashed',
        borderColor: dragOver ? 'primary.main' : 'divider',
        borderRadius: 2,
        p: 2,
        bgcolor: dragOver ? 'action.hover' : 'background.paper',
        minHeight: 160,
        display: 'flex',
        flexDirection: 'column',
      }}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (!disabled) handleFiles(e.dataTransfer.files);
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {title}
      </Typography>
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          py: 1,
          cursor: disabled ? 'default' : 'pointer',
        }}
        onClick={() => !disabled && openPrimary()}
      >
        {icon === 'document' ? (
          <Description color="action" sx={{ fontSize: 36, mb: 0.5 }} />
        ) : (
          <CameraAlt color="action" sx={{ fontSize: 36, mb: 0.5 }} />
        )}
        <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
          {hint}
        </Typography>
        {uploading && <CircularProgress size={18} sx={{ mt: 1 }} />}
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {files.length === 0 ? (
        <Typography variant="caption" color="text.secondary" textAlign="center">
          {emptyLabel}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1 }}>
          {files.map((file) => (
            <Box
              key={file.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 1,
                py: 0.5,
              }}
            >
              <InsertDriveFile fontSize="small" color="action" />
              <Link
                href={downloadHref ? downloadHref(file) : undefined}
                component={onOpenFile && !downloadHref ? 'button' : undefined}
                target={downloadHref ? '_blank' : undefined}
                rel="noreferrer"
                underline="hover"
                sx={{ flexGrow: 1, fontSize: '0.8rem', minWidth: 0, textAlign: 'left' }}
                noWrap
                onClick={(e) => {
                  e.stopPropagation();
                  if (onOpenFile) {
                    e.preventDefault();
                    onOpenFile(file);
                  }
                }}
              >
                {file.file_name}
              </Link>
              {!disabled && onDelete && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(file);
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}
      <Box
        sx={{
          mt: 1,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        {capture && (
          <Button
            size="small"
            startIcon={<CameraAlt />}
            disabled={disabled || uploading}
            onClick={(e) => {
              e.stopPropagation();
              openCamera();
            }}
            sx={{ textTransform: 'none' }}
          >
            Take photo
          </Button>
        )}
        <Button
          size="small"
          startIcon={capture ? <PhotoLibrary /> : <CloudUpload />}
          disabled={disabled || uploading}
          onClick={(e) => {
            e.stopPropagation();
            openFilePicker();
          }}
          sx={{ textTransform: 'none' }}
        >
          Choose file
        </Button>
      </Box>
      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept={accept}
        disabled={disabled}
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = '';
        }}
      />
      {capture && (
        <input
          ref={cameraInputRef}
          type="file"
          hidden
          accept={accept}
          capture="environment"
          disabled={disabled}
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      )}
    </Box>
  );
};

export default MilestoneUploadCard;
